import { useCallback, useEffect, useState } from 'react';
import { isEmpty, isNil, noop, uniq } from 'lodash';
import { Wizard } from 'taboola-ultimate-ui';
import { STEPS } from 'modules/campaigns/modules/common-campaign-form/config/steps';
import { throwCriticalError } from 'modules/taboola-common-frontend-modules/formData/utils/throwCriticalError';
import { useCurrentValueGetter } from '../../../../hooks';
import { APP_EVENT_TYPE, useAppEventContext } from '../../app-events-aggregator';
import { useChatAgentContext } from '../../chat-agent/hooks/useChatAgentContext';
import { wizardStepIdValueStore } from '../utils/wizardStepValueStore';

export const useFormWizardSteps = ({
    validate,
    handleValidationError,
    optionalValidate,
    initialStep = STEPS.FIRST_STEP,
    skipChatContextRegistration,
}) => {
    const { registerWizard } = useChatAgentContext();
    const { push: pushAppEvent } = useAppEventContext();
    const [step, setStep] = useState(initialStep);
    //TODO: https://tbla.atlassian.net/browse/DEV-175560 remove this after fix Wizard component
    const getCurrentStep = useCurrentValueGetter(step);
    wizardStepIdValueStore.setValue(step);
    const [stepList, setStepList] = useState([]);
    const stepListGetter = useCurrentValueGetter(stepList);
    const registerStep = useCallback(newStep => {
        setStepList(prev => {
            const newList = uniq([...prev, newStep]);
            newList.sort();

            return newList;
        });

        setStep(step => {
            if (Wizard.getStepAsArray(newStep)[0] === step && Wizard.getStepAsArray(newStep).length > 1) {
                wizardStepIdValueStore.setValue(newStep);
                return newStep;
            }
            return step;
        });

        const unregister = newStep => {
            setStepList(prev => prev.filter(val => val !== newStep));
        };

        return unregister;
    }, []);

    const handleSetStep = useCallback(
        async (value, { skipValidation } = {}) => {
            if (isNil(value)) {
                throwCriticalError(new Error(`Unknown form step navigation ${value}`));
                return;
            }
            const newStep = `${value}`;
            if (newStep < step) {
                setStep(newStep);
                wizardStepIdValueStore.setValue(newStep);
                return;
            }
            const isFormValid = skipValidation || (await validate());
            if (!isFormValid) {
                handleValidationError();
                return;
            }
            const isOptionalValid = await optionalValidate(() => setStep(newStep), noop);
            if (isOptionalValid) {
                setStep(newStep);
                wizardStepIdValueStore.setValue(newStep);
            }
        },
        [handleValidationError, validate, optionalValidate, step, setStep]
    );
    const prevStep = useCallback(async () => {
        const step = getCurrentStep();
        if (isEmpty(stepList)) {
            if (step !== STEPS.FIRST_STEP) {
                await handleSetStep(`${+step - 1}`);
            }
            return;
        }
        const smartPrevList =
            step.length === 1 ? stepList : stepList.filter(val => val !== Wizard.getStepAsArray(step)[0]);
        const prevValue = smartPrevList[smartPrevList.findIndex(val => val === step) - 1];
        await handleSetStep(prevValue);
    }, [handleSetStep, stepList, getCurrentStep]);

    const nextStep = useCallback(
        async (event, { skipValidation } = {}) => {
            const step = getCurrentStep();
            if (isEmpty(stepList)) {
                await handleSetStep(`${+step + 1}`, { skipValidation });
                return;
            }
            const nextIndex = stepList.findIndex(val => val === step) + 1;
            const nextValue = stepList[nextIndex] || stepList[0];
            await handleSetStep(nextValue, { skipValidation });
        },
        [handleSetStep, stepList, getCurrentStep]
    );

    const onStepGetActive = useCallback(
        (stepId, stepLabel) => {
            pushAppEvent({
                type: APP_EVENT_TYPE.FORM_WIZARD_STEP,
                id: stepId,
                displayText: stepLabel,
            });
        },
        [pushAppEvent]
    );

    useEffect(() => {
        if (skipChatContextRegistration) {
            return;
        }
        const unregister = registerWizard({
            setWizardStep: (step = 1) => {
                setStep(prev => `${+prev + step}`);
                wizardStepIdValueStore.setValue(step);
            },
        });

        return unregister;
    }, [registerWizard, skipChatContextRegistration]);

    // we forget all sub steps for step higher than current for dynamic new registration
    const rootStep = Wizard.getStepAsArray(step)[0];
    useEffect(() => {
        if (isEmpty(stepListGetter())) {
            return;
        }
        setStepList(list =>
            list.filter(val => Wizard.getStepAsArray(val).length === 1 || Wizard.getStepAsArray(val)[0] <= rootStep)
        );
    }, [rootStep, stepListGetter]);

    return {
        step,
        setStep: handleSetStep,
        prevStep,
        nextStep,
        registerStep,
        onStepGetActive,
    };
};
